import { Grid, Image } from '@design-systems/canvas-ui'
import { ErrorAlert } from 'components/error-fallback'
import { BasicPlaceholder } from 'components/placeholder'
import {
    useGetBrandSales,
    useMultipleBrandsGreenfield,
} from 'hooks/brands/useBrandSales'
import useCountdown from 'hooks/utils/useCountdown'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { format } from 'date-fns'
import {
    GeneralizedComponent,
    GeneralizedComponentPayloadType,
    GeneralizedViewOption,
} from 'components/generalized-component/generalized-component'
import { isCompany, isStore } from 'utils/location'
import { Brand } from './brands'
import {
    buildSalesByLocationTableData,
    SalesData,
    formatDayDataAsList,
    safePickColumns,
    transformTableData,
} from './helpers'
import {
    addHyperlinkToFirstColumn,
    Cell,
    filterRows,
    pickColumns,
    TableData,
    TitledTableData,
} from 'utils/data'
import { useOutletContext } from 'react-router-dom'
import useRefresh from 'hooks/utils/useRefresh'
import BrandSalesCard from './brand-sales-card'
import {
    ItemOrClassEntry,
    SalesKeyValues,
    SalesPayload,
} from '../../queries/brands/brand-sales'
import {
    TimeFrames,
    useFacetedBrandsTrends,
} from 'hooks/brands/useBrandsTrends'
import { translateHeadings } from 'locales/utils'
import UltaBrandLoyalty from './brands-ulta-loyalty'
import { formatCurrency } from 'utils/currency-helper'

interface OutletContext {
    brand: Brand
}

export const BrandDetails = () => {
    const { brand } = useOutletContext<OutletContext>()
    const { filters: filtersKeys, brands } = TRANSLATION_KEYS
    const { t, currentLocation, logout, env, session, pathname } =
        useEssentials()
    const isStoreLocation = isStore(currentLocation)

    const isUltaBrand = brand?.brand_id?.toLowerCase() === 'ulta'
    const [timePeriodIndex, setTimePeriodIndex] = useState<number>(0)

    const brandSalesFilters = useMemo(
        () => ({
            location_id: isCompany(currentLocation) ? '' : currentLocation,
            brand_id: brand?.brand_ref_id_arr.join(','),
            brand_type: brand?.brand_mic_licensor,
            date: format(new Date(), 'yyyy-MM-dd'),
        }),
        [currentLocation, brand],
    )
    const brandsTrendsFilters = useMemo(() => {
        const base = {
            brand_id: brand?.brand_ref_id_arr.join(','),
            brand_type:
                brand?.brand_mic_licensor?.trim().toLowerCase() === 'department'
                    ? 'dept'
                    : brand?.brand_mic_licensor,
            start_date: brand?.start_date,
            end_date: brand?.end_date,
        }
        return isCompany(currentLocation)
            ? base
            : {
                  ...base,
                  location_id: currentLocation,
              }
    }, [currentLocation, brand])

    const {
        data,
        isLoading: brandIsLoading,
        isError: brandIsError,
        error,
        refetch,
        remove,
    } = useGetBrandSales({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        filters: brandSalesFilters,
        brand: brand,
    })

    const trendFrame = useMemo(() => {
        return TimeFrames[timePeriodIndex]
    }, [timePeriodIndex])

    const {
        data: trendsData,
        isLoading: trendsIsLoading,
        isError: trendsIsError,
        refetch: trendsRefetch,
        remove: trendsRemove,
        setTimeframe,
        error: trendsError,
    } = useFacetedBrandsTrends({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        filters: brandsTrendsFilters,
        frame: trendFrame,
    })
    useEffect(() => {
        setTimeframe(TimeFrames[timePeriodIndex])
    }, [timePeriodIndex, setTimeframe])

    const { brandLocation, brandMetrics } = useMultipleBrandsGreenfield({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        brand: brand,
    })

    const isLoading =
        brandIsLoading || brandMetrics?.isLoading || brandLocation?.isLoading

    // Combined error state across all queries used on this page
    const combinedIsError =
        brandIsError ||
        trendsIsError ||
        brandMetrics?.isError ||
        brandLocation?.isError

    // Prefer returning the error that corresponds to the failing query(s).
    // Order determines precedence when multiple queries have errors.
    const combinedError =
        (brandIsError && error) ||
        (trendsIsError && trendsError) ||
        (brandMetrics?.isError && brandMetrics.error) ||
        (brandLocation?.isError && brandLocation.error) ||
        null

    const formattedDayData = useMemo<{
        formattedData: SalesData[]
        totalSales: number | null
    }>(() => {
        if (!Array.isArray(data) || data.length === 0) {
            return { formattedData: [], totalSales: null }
        }

        const timePeriodKey = SalesKeyValues[timePeriodIndex]

        const timePeriodData = data[0]?.[timePeriodKey]

        if (!timePeriodData) {
            return { formattedData: [], totalSales: null }
        }

        return formatDayDataAsList(timePeriodData, t)
    }, [data, t, timePeriodIndex])

    const selectedTimePeriodData = useMemo(() => {
        if (
            !brandMetrics?.data ||
            !Array.isArray(brandMetrics.data.rows) ||
            brandMetrics.data.rows.length === 0
        ) {
            return { formattedData: [], totalSales: null }
        }

        const columnMapping = {
            1: transformTableData(
                pickColumns(brandMetrics.data, [0, 10, 12, 11]),
                t,
            ),
            2: transformTableData(
                pickColumns(brandMetrics.data, [0, 1, 8, 2]),
                t,
            ),
            3: transformTableData(
                pickColumns(brandMetrics.data, [0, 3, 7, 4]),
                t,
            ),
            4: transformTableData(
                pickColumns(brandMetrics.data, [0, 5, 9, 6]),
                t,
            ),
        }

        const result = columnMapping[
            timePeriodIndex as keyof typeof columnMapping
        ] || {
            formattedData: [],
            totalSales: null,
        }

        return result
    }, [brandMetrics?.data, t, timePeriodIndex])

    const generalizedComponentData: GeneralizedComponentPayloadType =
        useMemo(() => {
            const processTableData = (tableData: TableData) => {
                if (!isStoreLocation) {
                    return addHyperlinkToFirstColumn(
                        tableData,
                        currentLocation,
                        pathname,
                        1,
                    )
                }
                return tableData
            }

            const todayData =
                Array.isArray(data) && data[0]?.sales_by_location
                    ? data[0].sales_by_location
                    : { day: [] }

            const hasValidDataForToday =
                Array.isArray(data) && data.length > 0 && !data[0]?.is_empty

            return {
                [t(filtersKeys.viewInDollars)]: [
                    {
                        title: 'today',
                        subtitle: '',
                        data: {
                            data: processTableData(
                                buildSalesByLocationTableData(
                                    hasValidDataForToday
                                        ? todayData
                                        : { day: [] },
                                    t,
                                ),
                            ),
                        },
                    },
                    {
                        title: 'yesterday',
                        subtitle: '',
                        data: {
                            data: processTableData(
                                safePickColumns(
                                    brandLocation?.data || { rows: [] },
                                    [0, 1, 2, 3],
                                    t,
                                ),
                            ),
                        },
                    },
                    {
                        title: 'week_to_date',
                        subtitle: '',
                        data: {
                            data: processTableData(
                                safePickColumns(
                                    brandLocation?.data || { rows: [] },
                                    [0, 4, 5, 6],
                                    t,
                                ),
                            ),
                        },
                    },
                    {
                        title: 'month_to_date',
                        subtitle: '',
                        data: {
                            data: processTableData(
                                safePickColumns(
                                    brandLocation?.data || { rows: [] },
                                    [0, 7, 8, 9],
                                    t,
                                ),
                            ),
                        },
                    },
                    {
                        title: 'year_to_date',
                        subtitle: '',
                        data: {
                            data: processTableData(
                                safePickColumns(
                                    brandLocation?.data || { rows: [] },
                                    [0, 10, 11, 12],
                                    t,
                                ),
                            ),
                        },
                    },
                ],
            }
        }, [
            isStoreLocation,
            t,
            data,
            brandLocation?.data,
            filtersKeys.viewInDollars,
            currentLocation,
            pathname,
        ])

    useRefresh(refetch, remove)
    useRefresh(trendsRefetch, trendsRemove)

    // Use the combined error when starting the logout countdown
    const count = useCountdown(5, logout, combinedError as Error)

    // Retry only the failing queries
    const handleRetry = useCallback(() => {
        if (brandIsError) {
            void refetch()
        }
        if (trendsIsError) {
            void trendsRefetch()
        }
        if (brandMetrics?.isError) {
            void brandMetrics.refetch()
        }
        if (brandLocation?.isError) {
            void brandLocation.refetch()
        }
    }, [
        brandIsError,
        trendsIsError,
        brandMetrics,
        brandLocation,
        refetch,
        trendsRefetch,
    ])

    if (isLoading) {
        return (
            <div className="placeholder-container">
                <BasicPlaceholder type="rect" height={'200px'} />
                <BasicPlaceholder type="text" rows={5} columns={3} />
            </div>
        )
    }

    if (combinedIsError || !data) {
        return (
            <ErrorAlert
                error={combinedError as Error}
                count={count}
                onRetry={handleRetry}
            />
        )
    }

    const payload =
        trendFrame === 'TD'
            ? adaptTodayBrandsToGeneralized(data || [], trendsData || [], t)
            : adaptBrandsTrendsToGeneralized(trendsData || [], t)

    return (
        <Grid.Container justify="center" className="brand-sales hc8-pt-4x">
            <Grid.Item
                size={{
                    xs: 10,
                    md: 4,
                    xl: 2,
                }}
            >
                <Image
                    src={encodeURI(brand.image_url).replace(/'/g, '%27')}
                    alt={brand.brand_name}
                    width="100%"
                    height="200px"
                />
            </Grid.Item>
            <Grid.Item
                className="hc8-pa-2x brand-sales"
                size={{
                    xs: 12,
                }}
            >
                <GeneralizedComponent
                    payload={generalizedComponentData || {}}
                    isLoading={isLoading}
                    widths={[70, 90, 90, 100]}
                    expandedWidth={false}
                    parentHandleClick={(timePeriodIndex: number) => {
                        setTimePeriodIndex(timePeriodIndex)
                    }}
                    hideTable={isStoreLocation ? true : false}
                    kpiName="brands"
                    isTimePeriodSticky={false}
                >
                    {timePeriodIndex === 0 ? (
                        <>
                            <BrandSalesCard
                                data={formattedDayData.formattedData}
                                totalSales={formattedDayData.totalSales}
                                isIntraDay={true}
                            />
                        </>
                    ) : (
                        <>
                            <BrandSalesCard
                                data={selectedTimePeriodData.formattedData}
                                totalSales={selectedTimePeriodData.totalSales}
                                isIntraDay={false}
                            />
                        </>
                    )}
                </GeneralizedComponent>
            </Grid.Item>
            {isUltaBrand && (
                <Grid.Item
                    size={{
                        xs: 12,
                    }}
                >
                    <UltaBrandLoyalty timePeriodIndex={timePeriodIndex} />
                </Grid.Item>
            )}
            <Grid.Item
                className="hc8-ph-2x hc8-pt-none hc8-pb-8x brand-trends"
                size={{
                    xs: 12,
                }}
            >
                {
                    <GeneralizedComponent
                        payload={payload}
                        isLoading={brandIsLoading || trendsIsLoading}
                        loadingComponent={
                            <BasicPlaceholder type="table" rows={5} />
                        }
                        isError={trendsIsError}
                        title={t(brands.trend)}
                        kpiName="brands"
                    />
                }
            </Grid.Item>
        </Grid.Container>
    )
}

const emptyTable: TitledTableData = {
    title: '',
    table: {
        // if the empty table makes it to `translateHeading` the
        // cell count has to match, thus 3 cells here.
        headings: [{ value: '' }, { value: '' }, { value: '' }],
        rows: [],
    },
}

// general structure for top/bottom tabbed table views
const buildTopBottomView = (
    top: TableData,
    bottom: TableData,
    t: (key: string) => string,
): GeneralizedViewOption => {
    const { brands } = TRANSLATION_KEYS
    return {
        title: '',
        subtitle: '',
        data: {
            [t(brands.tabs.top)]: top,
            [t(brands.tabs.bottom)]: bottom,
        },
    }
}

// adapter for table data from /brands/trends call
const adaptBrandsTrendsToGeneralized = (
    data: TitledTableData[],
    t: (key: string) => string,
): GeneralizedComponentPayloadType => {
    const { filters: filtersKeys, brands } = TRANSLATION_KEYS
    const {
        itemDescription,
        subclass: subclassHeading,
        quantity,
        sales,
    } = brands.headings
    const subclass = data[0] || emptyTable
    const item = data[1] || emptyTable
    const topPredicate = (_: unknown, index: number) => index < 5
    const bottomPredicate = (_: unknown, index: number) => index >= 5
    const itemTable = translateHeadings(
        [itemDescription, quantity, sales],
        t,
        item.table,
    )
    const subclassTable = translateHeadings(
        [subclassHeading, quantity, sales],
        t,
        subclass.table,
    )
    // TODO - language
    return {
        [t(filtersKeys.viewSubclass)]: [
            buildTopBottomView(
                filterRows(subclassTable, topPredicate),
                filterRows(subclassTable, bottomPredicate),
                t,
            ),
        ],
        [t(filtersKeys.viewItem)]: [
            buildTopBottomView(
                filterRows(itemTable, topPredicate),
                filterRows(itemTable, bottomPredicate),
                t,
            ),
        ],
    }
}

// adapter for table data from brand_sales calls,
// reuses headings from /brands/trends response.
const adaptTodayBrandsToGeneralized = (
    sales: SalesPayload,
    baseData: TitledTableData[],
    t: (key: string) => string,
): GeneralizedComponentPayloadType => {
    const { filters: filtersKeys, brands } = TRANSLATION_KEYS
    const {
        itemDescription,
        subclass: subclassHeading,
        quantity,
        sales: salesHeading,
    } = brands.headings
    const payload = sales[0] || emptyTable
    const subclass = baseData[0] || emptyTable
    const item = baseData[1] || emptyTable
    const itemTop: Cell[][] = filterAndSortItemClassEntry(
        payload.brand_item?.subItemToday ?? [],
        'Top',
    )
    const itemBottom: Cell[][] = filterAndSortItemClassEntry(
        payload.brand_item?.subItemToday ?? [],
        'Bottom',
    )
    const classTop: Cell[][] = filterAndSortItemClassEntry(
        payload.brand_class?.subClassToday ?? [],
        'Top',
    )
    const classBottom: Cell[][] = filterAndSortItemClassEntry(
        payload.brand_class?.subClassToday ?? [],
        'Bottom',
    )
    const translatedItemTable = translateHeadings(
        [itemDescription, quantity, salesHeading],
        t,
        item.table,
    )
    const translatedSubclassTable = translateHeadings(
        [subclassHeading, quantity, salesHeading],
        t,
        subclass.table,
    )
    // TODO - language
    return {
        [t(filtersKeys.viewSubclass)]: [
            buildTopBottomView(
                {
                    headings: translatedSubclassTable.headings,
                    rows: classTop,
                },
                {
                    headings: translatedSubclassTable.headings,
                    rows: classBottom,
                },
                t,
            ),
        ],
        [t(filtersKeys.viewItem)]: [
            buildTopBottomView(
                {
                    headings: translatedItemTable.headings,
                    rows: itemTop,
                },
                {
                    headings: translatedItemTable.headings,
                    rows: itemBottom,
                },
                t,
            ),
        ],
    }
}

// table row logic for data from /brand_sales call
const filterAndSortItemClassEntry = (
    entries: ItemOrClassEntry[],
    byType: 'Top' | 'Bottom',
): Cell[][] => {
    return (
        entries
            .filter((x) => x.type === byType)
            // top shows highest to lowest
            // bottom shows lowest to highest
            .sort((x, y) => x.rank - y.rank)
            .map((x) => [
                // the cell mapping matches the order given by
                // the /brands/trends call. If that order changes
                // this will also need to change. I.E name, qty, sales.
                { value: x.name },
                { value: x.sales_quantity.toString() },
                { value: formatCurrency(x.sales) },
            ])
    )
}
